import React, {useCallback, useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Form, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {AxiosError} from 'axios'
import {getPaymentMethods, patchPaymentMethod} from '../api/paymentMethodsApi'

type PaymentMethodRow = {
  _id: string
  code: string
  description?: string
  isActive?: boolean
  type?: 'online' | 'cash' | 'other'
  translations?: Array<{language?: string; name?: string; description?: string}>
}

function displayName(m: PaymentMethodRow) {
  const tr = m.translations?.find((t) => t.language === 'en') || m.translations?.[0]
  return tr?.name || tr?.description || m.description || m.code
}

function lastActiveCode(err: unknown): boolean {
  const data = (err as AxiosError<{message?: {code?: string} | string; code?: string}>)?.response
    ?.data
  const msg = data?.message
  if (typeof msg === 'object' && msg !== null && 'code' in msg) {
    return msg.code === 'LAST_ACTIVE_PAYMENT_METHOD'
  }
  if (data?.code === 'LAST_ACTIVE_PAYMENT_METHOD') return true
  if (typeof msg === 'string' && msg.includes('LAST_ACTIVE_PAYMENT_METHOD')) return true
  return false
}

const PaymentMethodsTab: React.FC = () => {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<PaymentMethodRow[]>([])
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const res = await getPaymentMethods(0, 200)
      setRows(res?.data || [])
    } catch {
      toast.error(intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.ERROR.LOAD'}))
    } finally {
      setLoading(false)
    }
  }, [intl])

  useEffect(() => {
    load()
  }, [load])

  const onToggleActive = async (m: PaymentMethodRow, next: boolean) => {
    if (m.isActive === next) return
    setBusyId(m._id)
    try {
      await patchPaymentMethod(m._id, {isActive: next})
      toast.success(intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.SUCCESS.UPDATE'}))
      await load()
    } catch (e) {
      if (lastActiveCode(e)) {
        toast.error(intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.ERROR.LAST_ACTIVE'}))
      } else {
        toast.error(intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.ERROR.UPDATE'}))
      }
    } finally {
      setBusyId(null)
    }
  }

  const onTypeChange = async (m: PaymentMethodRow, type: 'online' | 'cash' | 'other') => {
    if (m.type === type) return
    setBusyId(m._id)
    try {
      await patchPaymentMethod(m._id, {type})
      toast.success(intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.SUCCESS.UPDATE'}))
      await load()
    } catch {
      toast.error(intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.ERROR.UPDATE'}))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className='card card-custom'>
      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='card-label'>{intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.TITLE'})}</h3>
        </div>
      </div>
      <div className='card-body pt-0'>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center min-h-200px'>
            <Spinner animation='border' variant='primary' />
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-5'>
              <thead>
                <tr className='text-start text-muted fw-bold fs-7 text-uppercase gs-0'>
                  <th>{intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.COL_NAME'})}</th>
                  <th>{intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.COL_CODE'})}</th>
                  <th>{intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.COL_TYPE'})}</th>
                  <th>{intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.COL_ACTIVE'})}</th>
                </tr>
              </thead>
              <tbody className='text-gray-600 fw-semibold'>
                {rows.map((m) => (
                  <tr key={m._id}>
                    <td>{displayName(m)}</td>
                    <td>
                      <span className='badge badge-light fw-bold'>{m.code}</span>
                    </td>
                    <td style={{minWidth: '140px'}}>
                      <Form.Select
                        size='sm'
                        value={m.type || 'other'}
                        disabled={busyId === m._id}
                        onChange={(e) =>
                          onTypeChange(m, e.target.value as 'online' | 'cash' | 'other')
                        }
                      >
                        <option value='online'>
                          {intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.TYPE_ONLINE'})}
                        </option>
                        <option value='cash'>
                          {intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.TYPE_CASH'})}
                        </option>
                        <option value='other'>
                          {intl.formatMessage({id: 'TOUR_PAYMENT_METHODS.TYPE_OTHER'})}
                        </option>
                      </Form.Select>
                    </td>
                    <td>
                      <Form.Check
                        type='switch'
                        checked={m.isActive !== false}
                        disabled={busyId === m._id}
                        onChange={(e) => onToggleActive(m, e.target.checked)}
                        id={`pm-active-${m._id}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default PaymentMethodsTab
